import { echeqRulesList, ruleActionPre } from "middleware/echeqRules";
import { call, put, takeLatest } from "redux-saga/effects";

import { actions as notificationActions } from "reducers/notification";

import { types as alterTypes } from "reducers/echeqRule";
import { types } from "reducers/echeqRules";

import { routerActions } from "react-router-redux";
import * as i18n from "util/i18n";
import { VALIDATION_ERROR } from "util/responses";

const sagas = [
    takeLatest(types.REQUEST_RULES_LIST, requestRulesList),
    takeLatest(types.REQUEST_RULE_ACTION_PRE, requestRuleActionPre),
];

export default sagas;

function* requestRulesList({ filters, isDesktop }) {
    const response = yield call(echeqRulesList, filters);
    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["echeqRules"]));
        yield put({
            type: types.FAILED_RULES_LIST,
        });
    } else {
        const {
            rules,
            totalPages,
            pageNumber,
            executionDay,
            executionHour,
            conditions,
            subConditionsAmount,
            subConditionsIssuer,
            subConditionsReceived,
        } = response.data.data;
        const processText = executionDay
            ? i18n.get("echeqs.rule.list.daemonMessage", null, {
                  EXECUTION_DAY: executionDay,
                  EXECUTION_HOUR: executionHour,
              })
            : null;
        yield put({
            type: types.SUCCESS_RULES_LIST,
            rules,
            totalPages,
            pageNumber,
            processText,
            isDesktop,
            conditions,
            subConditionsAmount,
            subConditionsIssuer,
            subConditionsReceived,
            activeSection: filters?.activeSection,
            filters,
        });
    }
}

function* requestRuleActionPre({ summarySelectedRule, pageNumber, isDesktop }) {
    const { selectedRule, orderedList, action } = summarySelectedRule;
    const response = yield call(ruleActionPre, selectedRule, orderedList, action, pageNumber);
    if (response.type === "W") {
        if (response.data.code === VALIDATION_ERROR) {
            if (response.data?.data?.conflictMessage) {
                yield put(
                    notificationActions.showNotification(
                        response.data?.data?.conflictMessage,
                        "warning",
                        ["echeqRules"],
                        null,
                        false,
                    ),
                );
            } else if (response.data?.data?.fixErrorMessage) {
                yield put(
                    notificationActions.showNotification(
                        response.data?.data?.fixErrorMessage,
                        "error",
                        ["echeqRules"],
                        null,
                        false,
                    ),
                );
            } else {
                yield put(notificationActions.showNotification(response.data?.data?.message, "error", ["echeqRules"]));
            }
        } else {
            yield put(
                notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["echeqRules"]),
            );
        }

        yield put({
            type: types.FAILED_RULE_ACTION_PRE,
        });
    } else {
        const beneficiary = response.data.data?.beneficiary || {};
        // eslint-disable-next-line camelcase
        const { beneficiario_documento, beneficiario_razon_social } = beneficiary;
        switch (action) {
            case "seeErrors": {
                const { echeqsWithError, totalPages, pageNumber: pn } = response.data?.data;
                yield put({
                    type: types.SUCCESS_RULE_ACTION_PRE,
                    summarySelectedRule,
                    errorsPagination: { totalPages, pageNumber: pn },
                    echeqsWithError: echeqsWithError.map((item) => ({
                        ...item,
                        sameTitular: item.echeqStatus === "ACTIVO",
                        number: item.echeqNumber,
                        amount: item.echeqAmount,
                        paymentDate: item.echeqPaymentDate,
                        character: item.echeqCharacter,
                        reference: "",
                        status: item.echeqStatus,
                        recibedBy: item.emiName,
                        recibedDocument: item.emiDocument,
                    })),
                    isDesktop,
                });
                break;
            }
            case "create":
                yield put({
                    type: alterTypes.ECHEQ_RULE_SET_INITIAL_VALUES,
                    actionType: action,
                    initialValues: null,
                });
                yield put(routerActions.push("/v2/echeqRules/create"));
                break;
            case "modify":
            case "copy": {
                yield put({
                    type: alterTypes.ECHEQ_RULE_SET_INITIAL_VALUES,
                    actionType: action,
                    initialValues: {
                        numberRule: selectedRule?.idRule,
                        account: selectedRule.cbu,
                        action: {
                            label: i18n.get(`manage.echeq.rules.message.${selectedRule.actionType}`),
                            value: selectedRule.actionType,
                        },
                        name: selectedRule.ruleName,
                        conditions: selectedRule.conditions?.map((item) => ({
                            id: item.id,
                            type: item.condition,
                            operator: item?.subCondition,
                            value: item?.value,
                        })),
                        currency: selectedRule?.currency,
                        includesAdmission: selectedRule?.automateAdmission,
                        address: selectedRule?.address,
                        document: selectedRule?.document,
                        reference: selectedRule?.reference,
                        referenceValue: selectedRule?.referenceValue,
                        beneficiary: ["PAY", "DISCOUNT"].includes(selectedRule.actionType)
                            ? {
                                  // eslint-disable-next-line camelcase
                                  name: beneficiario_razon_social || i18n.get("global.bankName"),
                                  // eslint-disable-next-line camelcase
                                  document: beneficiario_documento || undefined,
                                  documentType: "CUIT / CUIL / CDI",
                              }
                            : undefined,
                    },
                });
                yield put(routerActions.push("/v2/echeqRules/create"));
                break;
            }
            case "enable":
            case "disable":
            case "delete": {
                const newSummary = {
                    ...summarySelectedRule,
                    selectedRule: {
                        ...summarySelectedRule.selectedRule,
                        beneficiary: ["PAY", "DISCOUNT"].includes(selectedRule.actionType)
                            ? {
                                  // eslint-disable-next-line camelcase
                                  name: beneficiario_razon_social || i18n.get("global.bankName"),
                                  // eslint-disable-next-line camelcase
                                  document: beneficiario_documento || undefined,
                                  documentType: "CUIT / CUIL / CDI",
                              }
                            : undefined,
                    },
                };
                yield put({
                    type: types.SUCCESS_RULE_ACTION_PRE,
                    summarySelectedRule: newSummary,
                    echeqsWithError: [],
                });
                yield put(routerActions.push(`/echeqRules/confirm/${action}`));
                break;
            }
            case "reorder": {
                yield put({
                    type: types.SUCCESS_RULE_ACTION_REORDER,
                });
                break;
            }
            default:
                break;
        }
    }
}
